import React, { createContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AuthService, REFRESH_OK, REFRESH_TERMINAL } from "../services/authservice";
import { showToast } from "../utils/showToast";
import { useNavigate } from "react-router-dom";
import jwtDecode from "jwt-decode";
import { getUser } from "../api/user";
import { startSessionKeeper } from "../services/sessionKeeper";
import { expireSessionFromTerminal } from "../services/authFetch";
import { rbacClaimsFromToken } from "../utils/permissions";

export const AuthContext = createContext();

// Referencia estable para "sin permisos": evita re-renders de los consumidores.
const NO_PERMISSIONS = Object.freeze([]);

export default function AuthProvider({ children }) {
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    checkUserLogin(setUser, setIsLoading, setUserData, userData);
  }, []);

  // Refresco proactivo del access (1 h) mientras haya sesión; ver sessionKeeper.
  const hasUser = !!user;
  useEffect(() => {
    if (!hasUser) return undefined;
    const currentUserId = user?.id;
    return startSessionKeeper({
      onSessionEnded: ({ reason }) => {
        setUser(null);
        if (reason === "other-tab") {
          // Otra pestaña cerró sesión: esta solo se alinea, sin toast de "expiró".
          AuthService.logout();
          navigate("/login");
        } else {
          expireSessionFromTerminal(reason);
        }
      },
      onTokensChanged: () => {
        // Otra pestaña inició sesión con OTRA cuenta: esta pestaña mostraría datos de la anterior.
        const token = AuthService.getAccessTokenApi();
        if (!token || !currentUserId) return;
        try {
          const decoded = jwtDecode(token);
          if (decoded.id !== currentUserId) {
            window.location.reload();
            return;
          }
          // Misma cuenta con token renovado: se actualizan solo los claims de RBAC,
          // sin tocar el resto del estado. Si no cambiaron se conserva la referencia.
          const next = rbacClaimsFromToken(decoded);
          setUser((prev) => {
            if (!prev) return prev;
            const same =
              prev.customRole?.id === next.customRole?.id &&
              prev.customRole?.name === next.customRole?.name &&
              prev.permissions.length === next.permissions.length &&
              prev.permissions.every((k, i) => k === next.permissions[i]);
            return same ? prev : { ...prev, ...next };
          });
        } catch {
          // Token ilegible: el flujo normal de 401 se encarga.
        }
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasUser]);

  useEffect(() => {
    const deliveryRoleId = "66edc29a13059690d19d6ea9";
    const deliveryCommerceFleetRoleId = "694215139c2e62f246ebe1e0";
    if (user && (user.role === deliveryRoleId || user.role === deliveryCommerceFleetRoleId)) {
      showToast(t("Sólo los usuarios de tipo cliente cuentan con acceso a este sistema"), false);
      AuthService.logout();
      navigate("/login");
    }
  }, [user, navigate]);

  return (
    <AuthContext.Provider
      value={{
        user,
        userData,
        isLoading,
        permissions: user?.permissions ?? NO_PERMISSIONS,
        customRole: user?.customRole ?? null,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

async function checkUserLogin(setUser, setIsLoading, setUserData, userData) {
  // "Fresco" = vigente con margen de 5 min: si no, se renueva antes de armar la sesión.
  const accessToken = AuthService.getFreshAccessTokenSync();

  if (!accessToken) {
    const refreshToken = AuthService.getRefreshTokenApi();

    if (!refreshToken) {
      AuthService.logout();
      setUser(null)
      setIsLoading(false)
    } else {
      AuthService.refreshSession(refreshToken)
        .then(({ status }) => {
          if (status === REFRESH_OK) {
            // Después de refrescar el token, intentar obtener el usuario nuevamente
            checkUserLogin(setUser, setIsLoading, setUserData);
            return;
          }
          // Sólo un rechazo explícito del servidor destruye las credenciales.
          // Ante un fallo transitorio se muestra el login pero el storage queda
          // intacto, y un reintento posterior puede rescatar la sesión.
          //
          // Este `return` además corta la recursión: antes se volvía a llamar a
          // checkUserLogin pasara lo que pasara, y sólo terminaba porque el
          // propio refresh borraba el storage al fallar. Con el fallo
          // transitorio ya no borrando nada, sin esto un backend caído dejaría
          // la función llamándose a sí misma indefinidamente.
          if (status === REFRESH_TERMINAL) AuthService.logout();
          setUser(null);
          setIsLoading(false);
        })
        .catch(() => {
          setUser(null);
          setIsLoading(false);
        });
    }
  } else {
    const user = jwtDecode(accessToken);
    // `role` queda como _id por contrato histórico (todas las compuertas
    // comparan ids). `roleType` conserva el tipo para las que deciden por tipo
    // de cuenta: useAuthScope no sirve en una vista que monta antes de que este
    // arranque renueve un token vencido, porque memoriza el scope vacío.
    // `permissions` (string[]) y `customRole` ({id, name} | null) viajan en el
    // JWT desde R3.2; se normalizan para que un token viejo sin esos claims
    // dé "sin permisos" (falla cerrado) en vez de undefined.
    setUser({
      ...user,
      role: user.role._id,
      roleType: user.role.type,
      ...rbacClaimsFromToken(user),
    })
    if (user.id && !userData) {
      try {
        const response = await getUser({ id: user.id });
        // Solo establecer userData si la respuesta es exitosa y no tiene error
        if (response && response.data && !response.error) {
          setUserData(response.data);
        } else if (response?.error) {
          const status = response.error?.statusCode || response.error?.status;
          if (status === 401 || status === 403) {
            // Token inválido o sesión revocada: cerrar sesión
            AuthService.logout();
            setUser(null);
          } else {
            // Error no relacionado con auth (CORS, red): continuar con datos del token
            console.warn("No se pudo obtener datos adicionales del usuario, usando datos del token");
          }
        }
      } catch (error) {
        // Manejar errores silenciosamente, especialmente errores de CORS
        if (error.isCorsError || error.corsSilent || (!error.response && error.message)) {
          // Error de red/CORS: continuar con datos del token
          console.warn("Error de conexión al obtener datos del usuario, usando datos del token");
        } else if (error.response?.status === 401 || error.response?.status === 403) {
          // Token inválido o sesión revocada: cerrar sesión
          AuthService.logout();
          setUser(null);
        } else {
          console.error("Error al obtener datos del usuario:", error);
        }
      }
    }
    setIsLoading(false)
  }
}

/**
 * Returns an object containing information about the authentication status and role of the current user.
 *
 * @returns {Object} An object with the following properties:
 * - isAuthenticated: A boolean indicating whether the user is authenticated or not.
 * - role: The role of the authenticated user.
 */
export function useAuthToken() {
  const getToken = () => AuthService.getAccessTokenApi();
  const getUserData = () => {
    const token = getToken();
    if (!token) return null;
    try {
      return jwtDecode(token);
    } catch (error) {
      console.error("Failed to decode token", error);
      return null;
    }
  };

  const userData = getUserData();

  return {
    isAuthenticated: !!userData,
    role: userData?.role._id,
  };
}